import { faXmark } from "@fortawesome/pro-solid-svg-icons"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { Button, Divider, InputAdornment, TextField, Typography } from "@mui/material"
import { Box } from "@mui/system"
import { DataGrid, GridColDef } from "@mui/x-data-grid"
import { IAdvertiserDomain } from "advertiser/model"
import { advertiserRPO } from "advertiser/repository"
import {
  createAdvertiserDomain,
  editAdvertiserDomain,
  validateAdvertiserDomain
} from "advertiser/tools/advertiser-domain"
import { translate } from "app/language/service"
import { messageSVC } from "message/service"
import { normalizeSync } from "normalize-diacritics"
import React from "react"
import { FlexBox } from "ui/components/FlexBox"
import { LinearLoader } from "ui/components/LinearLoader"
import { ListIdCell } from "ui/components/ListIdCell"
import { getDataGridTexts } from "ui/tools/list"
import { AdvertiserDomainListActions } from "./AdvertiserDomainListActions"

interface IAdvertiserDomainListProps {
  advertiserId: number | undefined
}

export const AdvertiserDomainList = React.memo(({ advertiserId }: IAdvertiserDomainListProps): JSX.Element => {
  const [advertiserDomains, setAdvertiserDomains] = React.useState<IAdvertiserDomain[] | null>(null)
  const [nameFilter, setNameFilter] = React.useState("")
  const [isLoading, setIsLoading] = React.useState(false)
  const [hasLoadingError, setHasLoadingError] = React.useState(false)
  const isCreation = typeof advertiserId !== "number"
  const onEdit = (updatedAvertiserDomain: IAdvertiserDomain) =>
    setAdvertiserDomains(
      (advertiserDomains as IAdvertiserDomain[]).map(a =>
        a.id === updatedAvertiserDomain.id ? updatedAvertiserDomain : a
      )
    )
  const Header = React.useMemo(() => {
    return (
      <>
        <FlexBox fullWidth gap jc="space-between">
          <TextField
            label={translate("label.filter")}
            size="small"
            disabled={advertiserDomains === null || advertiserDomains.length === 0}
            value={nameFilter}
            onChange={event => setNameFilter(event.target.value)}
            InputProps={{
              endAdornment: nameFilter && (
                <InputAdornment
                  position="end"
                  sx={{
                    "& .icon": {
                      cursor: "pointer"
                    }
                  }}>
                  <FontAwesomeIcon icon={faXmark} className="icon" onClick={() => setNameFilter("")} />
                </InputAdornment>
              )
            }}
          />
          <Button
            variant="outlined"
            onClick={() => {
              messageSVC.confirmMessage({
                title: translate("advertiser-domain.add.title"),
                message: translate("advertiser-domain.message"),
                isInputMessage: true,
                confirmButtonLabel: translate("button.create"),
                validateInput: newDomain => validateAdvertiserDomain(advertiserDomains || [], newDomain),
                onSuccessValidation: async newDomain => {
                  try {
                    await createAdvertiserDomain(advertiserId as number, newDomain)
                    refreshList()
                    return ""
                  } catch (error) {
                    console.error(error)
                    return translate("advertiser-domain.add.error.server")
                  }
                },
                onSuccess: input => messageSVC.success(translate("advertiser-domain.add.success", input))
              })
            }}>
            {translate("button.add")}
          </Button>
        </FlexBox>
        <Divider sx={{ width: "100%" }} />
      </>
    )
  }, [advertiserId, advertiserDomains, nameFilter])
  const refreshList = () => {
    setIsLoading(true)
    advertiserRPO
      .getAdvertiserDomains(Number(advertiserId))
      .then(fetchedAdvertiserDomains => {
        setAdvertiserDomains(fetchedAdvertiserDomains)
        setIsLoading(false)
      })
      .catch(error => {
        console.error(error)
        setIsLoading(false)
        setHasLoadingError(true)
      })
  }
  React.useEffect(() => {
    if (isCreation) {
      return
    }
    refreshList()
  }, [advertiserId])
  if (isCreation) {
    return (
      <Typography fontSize=".9rem" color="textSecondary">
        {translate("advertiser-domain.creation.message")}
      </Typography>
    )
  }
  if (hasLoadingError) {
    return (
      <Typography fontSize=".9rem" color="error" sx={{ mt: "2rem" }}>
        {translate("label.list.error")}
      </Typography>
    )
  }
  if (!advertiserDomains || isLoading) {
    return <LinearLoader text={translate("advertiser.loading")} />
  }
  if (advertiserDomains.length === 0) {
    return (
      <>
        {Header}
        <Typography fontSize=".9rem" color="textPrimary">
          {translate("advertiser-domain.empty")}
        </Typography>
      </>
    )
  }
  const normalizedNameFilter = normalizeSync(nameFilter).toLowerCase()
  const filteredDomains = !nameFilter
    ? advertiserDomains
    : advertiserDomains.filter(d => d.normalizedName.indexOf(normalizedNameFilter) !== -1)
  const columns: Array<GridColDef<IAdvertiserDomain>> = [
    {
      field: "domain",
      headerName: translate("label.domain"),
      flex: 3,
      renderCell: params => (
        <ListIdCell
          label={params.row.domain}
          id={params.row.id}
          onClick={() => editAdvertiserDomain(advertiserDomains, params.row, onEdit)}
        />
      )
    },
    {
      field: "actions",
      headerName: translate("label.actions"),
      maxWidth: 100,
      sortable: false,
      flex: 1,
      renderCell: params => (
        <AdvertiserDomainListActions
          allAdvertiserDomains={advertiserDomains}
          advertiserDomain={params.row}
          onEdit={advertiserDomain => onEdit(advertiserDomain)}
          onDelete={advertiserDomainId =>
            setAdvertiserDomains(advertiserDomains.filter(a => a.id !== advertiserDomainId))
          }
        />
      )
    }
  ]
  return (
    <>
      {Header}
      <Box
        sx={{
          "width": "100%",
          "& > *": {
            width: "100%"
          },
          "& .MuiDataGrid-selectedRowCount": {
            visibility: "hidden"
          },
          "& .MuiDataGrid-cell": {
            fontSize: ".8rem",
            fontWeight: 300
          },
          "& .MuiDataGrid-row": {
            cursor: "default"
          },
          "& .MuiDataGrid-row.Mui-selected": {
            backgroundColor: "transparent"
          },
          "& .targeted-check": {
            "color": theme => theme.palette.text.primary2,
            "& svg": {
              ml: "1rem"
            }
          },
          "& .MuiDataGrid-row:hover, & .MuiDataGrid-row.Mui-selected:hover": {
            backgroundColor: "transparent !important"
          },
          "& .icon": {
            color: theme => theme.palette.text.primary2
          },
          "& .icon.non-targeted": {
            color: theme => theme.palette.text.secondary
          },
          "& .MuiDataGrid-columnHeader, & .MuiDataGrid-columnHeader:focus, & .MuiDataGrid-cell:focus, & .MuiDataGrid-cell":
            {
              outline: "none !important"
            }
        }}>
        <DataGrid
          rows={filteredDomains}
          columns={columns}
          disableColumnFilter
          disableColumnMenu
          className="advertiser-domain-list"
          rowHeight={50}
          autoPageSize
          localeText={getDataGridTexts()}
          sx={{ height: "22rem" }}
        />
      </Box>
    </>
  )
})
